import { resolveDisplayedSalesReps } from './displayedSalesRepResolution';
import { isAdministratorRole as isInheritedAdministratorRole } from './instanceRoleInheritance';

export const normalizeRoleToken = (role = '') =>
  String(role || '').trim().toLowerCase().replace(/[_\s]+/g, '-');

export const isAdministratorRole = (role = '') => {
  const normalized = normalizeRoleToken(role);
  return isInheritedAdministratorRole(normalized);
};

export const displayNameForUser = (u = {}) => {
  const user = u || {};
  return `${user.first_name || user.firstName || ''} ${user.last_name || user.lastName || ''}`.trim() ||
    user.name ||
    user.label ||
    user.display ||
    user.email ||
    '';
};

export const resolveAvatarForUser = (u = {}, normalizeAvatarUrl = (v) => v) => {
  const user = u || {};
  return normalizeAvatarUrl(user.profilePicture || user.avatar || user.photoUrl || user.avatarUrl || null);
};

const asArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const getUserId = (u = {}) => u.id ?? u.userId ?? u.repId ?? u.value ?? null;

export const collectAdminCandidatesForPo = (po = {}, so = {}) => {
  const pools = [
    ...asArray(po.orderAssignedUsers),
    ...asArray(po.Order?.AssignedTo),
    ...asArray(so.AssignedTo),
    ...asArray(po.salesOrder?.AssignedTo),
    ...asArray(po.assignedTo),
    ...asArray(so.assignedTo),
  ];

  return pools.filter((u) => isAdministratorRole(u?.role || u?.userRole || u?.type));
};

const uniqueUsers = (users = []) => {
  const seen = new Set();
  return users.filter((user) => {
    const key = getUserId(user) ?? displayNameForUser(user);
    if (!key || seen.has(String(key))) return false;
    seen.add(String(key));
    return true;
  });
};

export const resolvePurchasingDashboardReps = ({ po = {}, so = {}, repIndex = new Map(), normalizeAvatarUrl } = {}) => {
  const clientIdsArr = Array.isArray(po.clientRepIds) ? po.clientRepIds.map(String) : [];
  const orderIdsArr = Array.isArray(po.orderRepIds) ? po.orderRepIds.map(String) : [];

  const clientDetails = asArray(po.clientRepDetails);
  const orderDetails = asArray(po.orderRepDetails);
  const firstClientDetail = clientDetails[0] || null;
  const firstOrderDetail = orderDetails[0] || null;

  if (!clientIdsArr.length) {
    const singleClient =
      po.clientRepId ??
      po.client_rep_id ??
      firstClientDetail?.id ??
      so.clientRepId ??
      so.clientRep?.id ??
      null;
    if (singleClient != null) clientIdsArr.push(String(singleClient));
  }

  if (!orderIdsArr.length) {
    const singleOrder =
      po.orderRepId ??
      po.order_rep_id ??
      firstOrderDetail?.id ??
      so.orderRepId ??
      so.orderRep?.id ??
      null;
    if (singleOrder != null) orderIdsArr.push(String(singleOrder));
  }

  const usersForRole = (details, ids, namedUser, name) => {
    const users = uniqueUsers([
      ...details,
      ...ids.map((id) => repIndex.get(String(id)) || { id }),
      ...(namedUser ? [namedUser] : []),
    ]);
    return users.length ? users : (name ? [{ name }] : []);
  };

  const actualClientReps = usersForRole(
    clientDetails,
    clientIdsArr,
    so.clientRep,
    so.clientRepName
  );
  const actualOrderReps = usersForRole(
    orderDetails,
    orderIdsArr,
    so.orderRep,
    so.orderRepName
  );
  const administrators = uniqueUsers(collectAdminCandidatesForPo(po, so).map((user) => {
    const id = getUserId(user);
    return id != null ? (repIndex.get(String(id)) || user) : user;
  }));
  const displayed = resolveDisplayedSalesReps({
    clientReps: actualClientReps,
    orderReps: actualOrderReps,
    administrators,
  });
  const namesFor = (users) => users.map(displayNameForUser).filter(Boolean).join(', ') || '—';
  const resolvedClientRepObj = displayed.clientReps[0] || null;
  const resolvedOrderRepObj = displayed.orderReps[0] || null;
  const clientRepFallbackRole = actualClientReps.length
    ? null
    : (actualOrderReps.length ? 'order-rep' : (administrators.length ? 'administrator' : null));
  const clientRepFallbackSource = clientRepFallbackRole;

  return {
    clientRepObj: resolvedClientRepObj,
    clientRepDetails: displayed.clientReps,
    clientRepId: clientIdsArr[0] || null,
    clientRepIds: clientIdsArr,
    clientRepName: namesFor(displayed.clientReps),
    clientRepAvatar: resolvedClientRepObj ? resolveAvatarForUser(resolvedClientRepObj, normalizeAvatarUrl) : null,
    clientRepFallbackRole,
    clientRepFallbackSource,
    orderRepObj: resolvedOrderRepObj,
    orderRepDetails: displayed.orderReps,
    orderRepId: orderIdsArr[0] || null,
    orderRepIds: orderIdsArr,
    orderRepName: namesFor(displayed.orderReps),
    orderRepAvatar: resolvedOrderRepObj ? resolveAvatarForUser(resolvedOrderRepObj, normalizeAvatarUrl) : null,
  };
};
